/**
 * Trail / afterimage FX knobs — single knob file (4-layer config doctrine).
 *
 * Spec: docs/plans/fx-grammar-specs/trails-and-afterimages.md §6. Sibling of
 * MOVEMENT_TRAVEL_CONFIG (movementTravel.config.ts), which still owns the
 * dash-travel FEEL numbers; this file owns the trail-system budget/degrade
 * caps and the ribbon/ghost visual defaults.
 *
 * Degrade ladder (§6):
 *   1. over `maxActiveTrails`      → newest ribbon attach refused
 *   2. graphics budget ≥ 80%       → new ribbons refused, live ones run on
 *   3. ghost pool exhausted        → skip that interval's ghost emission
 * All refusals are counted (TrailSystem.getStats()) — starvation is
 * observable, never silent.
 */
import { MOVEMENT_TRAVEL_CONFIG } from './movementTravel.config';

export const TRAIL_FX_CONFIG = {
  /** Hard cap on concurrent ribbons — each is ONE graphics object, so this
   *  is ≤ 40% of the 30-graphics VFXBudgetManager budget worst-case. */
  maxActiveTrails: 12,
  /** Fixed afterimage-sprite pool size (sprites, not graphics — the 150
   *  shape budget class). A ghost "spawn" after warmup is setTexture +
   *  setPosition + setAlpha, never a new Pixi object. */
  maxGhosts: 24,
  /** Ghost emission cadence floor (ms) — intervals clamp up to this. */
  minGhostIntervalMs: 30,
  /** Refuse new ribbon attaches when graphics-budget utilization ≥ this. */
  graphicsBudgetRefuseFraction: 0.8,
  /** TrailStore observability sampling cadence per live ribbon (~4Hz). */
  sampleIntervalMs: 250,

  ribbon: {
    /** Head stroke width (px). */
    width: 5,
    /** How much position history the ribbon shows (ms). */
    lengthMs: 240,
    /** Ring-buffer sample cap per ribbon (≈ lengthMs / frame at 60fps). */
    maxPoints: 24,
    /** Just under the projectile body (Z inside the VFX depth vocabulary). */
    depth: 93,
    /** Core stroke alpha at the head. */
    alpha: 0.9,
  },

  ghost: {
    /** Instant blinks: ghosts placed along the blink line. */
    count: 3,
    /** Travel dashes: emit cadence (shares the dash-trail knob). */
    intervalMs: MOVEMENT_TRAVEL_CONFIG.trail.intervalMs,
    /** Per-ghost fade time (shares the dash-trail one-shot lifetime). */
    lifeMs: MOVEMENT_TRAVEL_CONFIG.trail.durationMs,
    startAlpha: 0.55,
    /** Default spectral tint (Q2 open — per-ability directives override). */
    tint: 0xffffff,
    /** Ghost display size (px) — mirrors PlayerRenderer's
     *  PLAYER_RENDER_SIZE (72); an art knob, not a derived value. */
    sizePx: 72,
    /** Just under the live player sprite. */
    depth: 88,
  },
} as const;
